// src/components/ReportWriter.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Font = Quill.import('formats/font');
Font.whitelist = [
  'arial', 'comic-sans', 'courier-new', 'georgia',
  'helvetica', 'lucida', 'times-new-roman', 'trebuchet-ms', 'verdana',
];
Quill.register(Font, true);

const Size = Quill.import('attributors/style/size');
export const customSizes = [
  '8px', '9px', '10px', '11px', '12px', '14px', '16px',
  '18px', '20px', '22px', '24px', '26px', '28px',
  '36px', '48px', '72px',
];
export const toolbarSizes = [false, ...customSizes];
Size.whitelist = customSizes;
Quill.register(Size, true);

const icons = Quill.import('ui/icons');
icons['insertTable'] =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>';

export const SCAN_ENUMS = [
  { value: 'ABDOMINAL', label: 'Abdominal' },
  { value: 'ABDOMEN_PELVIS_MALE', label: 'Abdomen & Pelvis (Male)' },
  { value: 'ABDOMEN_PELVIS_FEMALE', label: 'Abdomen & Pelvis (Female)' },
  { value: 'PELVIC_MALE', label: 'Pelvic (Male)' },
  { value: 'PELVIC_FEMALE', label: 'Pelvic (Female)' },
  { value: 'OBSTETRIC_EARLY', label: 'Obstetric (Early)' },
  { value: 'OBSTETRIC_LATE', label: 'Obstetric (Late)' },
  { value: 'OBSTETRIC_TWINS', label: 'Obstetric (Twins)' },
  { value: 'ANOMALY', label: 'Anomaly' },
  { value: 'BIOPHYSICAL_PROFILE', label: 'Biophysical Profile' },
  { value: 'TRANSABDOMINAL_PELVIC', label: 'Transabdominal Pelvic' },
  { value: 'TRANSVAGINAL', label: 'Transvaginal' },
  { value: 'ECHO_ADULT', label: 'Echo (Adult)' },
  { value: 'ECHO_PEDIATRIC', label: 'Echo (Pediatric)' },
  { value: 'MUSCULOSKELETAL', label: 'Musculoskeletal' },
  { value: 'NECK', label: 'Neck' },
  { value: 'THYROID', label: 'Thyroid' },
  { value: 'BREAST', label: 'Breast' },
  { value: 'CHEST', label: 'Chest' },
  { value: 'SCROTAL', label: 'Scrotal' },
  { value: 'PENILE', label: 'Penile' },
  { value: 'NEONATAL_HEAD', label: 'Neonatal Head' },
  { value: 'ARTERIAL_DOPPLER_BOTH_LOWER', label: 'Arterial Doppler (Both Lower)' },
  { value: 'ARTERIAL_DOPPLER_LEFT_LOWER', label: 'Arterial Doppler (Left Lower)' },
  { value: 'ARTERIAL_DOPPLER_RIGHT_LOWER', label: 'Arterial Doppler (Right Lower)' },
  { value: 'ARTERIAL_DOPPLER_LEFT_UPPER', label: 'Arterial Doppler (Left Upper)' },
  { value: 'ARTERIAL_DOPPLER_RIGHT_UPPER', label: 'Arterial Doppler (Right Upper)' },
  { value: 'VENOUS_DOPPLER_BOTH_LOWER', label: 'Venous Doppler (Both Lower)' },
  { value: 'VENOUS_DOPPLER_LEFT_LOWER', label: 'Venous Doppler (Left Lower)' },
  { value: 'VENOUS_DOPPLER_RIGHT_LOWER', label: 'Venous Doppler (Right Lower)' },
  { value: 'VENOUS_DOPPLER_LEFT_UPPER', label: 'Venous Doppler (Left Upper)' },
  { value: 'VENOUS_DOPPLER_RIGHT_UPPER', label: 'Venous Doppler (Right Upper)' },
  { value: 'GENERAL', label: 'General' },
];

const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isSmallDesktop: width >= 1024 && width < 1280,
  };
};

const readLS = (key, fallback = '') => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};

const ReportWriter = ({ userProfile }) => {
  const previewContainerRef = useRef(null);
  const contentToCopyRef = useRef(null);
  const findingsRef = useRef(null);
  const impressionRef = useRef(null);
  const recommendationRef = useRef(null);

  const { isMobile, isTablet, isSmallDesktop } = useViewport();

  // Prefer blob URL if we already have one; fall back to raw URL.
  const [letterheadUrl, setLetterheadUrl] = useState(() =>
    readLS('letterheadBlobUrl') || readLS('letterheadUrl')
  );
  const [signatureUrl, setSignatureUrl] = useState(() =>
    readLS('signatureBlobUrl') || readLS('signatureUrl')
  );
  const [hospitalName, setHospitalName] = useState(() =>
    readLS('hospitalName')
  );
  const [clinicianName, setClinicianName] = useState(() => {
    const fromProfile =
      userProfile?.fullName ||
      [userProfile?.firstName, userProfile?.lastName]
        .filter(Boolean)
        .join(' ');
    return (
      fromProfile ||
      readLS('clinicianName') ||
      readLS('clinicianFullName')
    );
  });
  const [clinicianDesignation, setClinicianDesignation] = useState(() => {
    return userProfile?.designation || readLS('clinicianDesignation');
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [tableModal, setTableModal] = useState({ isOpen: false, quill: null });
  const [tableCols, setTableCols] = useState(5);
  const [tableRows, setTableRows] = useState(2);
  const [rememberDims, setRememberDims] = useState(false);

  const [tableUI, setTableUI] = useState({
    visible: false,
    top: 0,
    left: 0,
    quillRef: null,
    tableNode: null,
  });
  const [tableMenuOpen, setTableMenuOpen] = useState(false);

  // ─── REACTIVE SYNC + AUTHENTICATED BLOB RESOLUTION ───
  useEffect(() => {
    let cancelled = false;

    const syncFromStorage = () => {
      setLetterheadUrl(readLS('letterheadBlobUrl') || readLS('letterheadUrl'));
      setSignatureUrl(readLS('signatureBlobUrl') || readLS('signatureUrl'));
      setHospitalName(readLS('hospitalName'));

      const fromProfile =
        userProfile?.fullName ||
        [userProfile?.firstName, userProfile?.lastName]
          .filter(Boolean)
          .join(' ');

      setClinicianName(
        fromProfile || readLS('clinicianName') || readLS('clinicianFullName')
      );
      setClinicianDesignation(
        userProfile?.designation || readLS('clinicianDesignation')
      );
    };

    const resolveAuthenticatedImages = async () => {
      try {
        const blobUrl = await api.fetchLetterheadBlobUrl();
        if (!cancelled && blobUrl) {
          setLetterheadUrl(blobUrl);
          try { localStorage.setItem('letterheadBlobUrl', blobUrl); } catch (_) {}
        }
      } catch (e) {
        console.warn('[ReportWriter] letterhead resolve failed:', e.message);
      }

      try {
        const raw = readLS('signatureUrl');
        const blobUrl = await api.fetchSignatureBlobUrl(raw);
        if (!cancelled && blobUrl) {
          setSignatureUrl(blobUrl);
          try { localStorage.setItem('signatureBlobUrl', blobUrl); } catch (_) {}
        }
      } catch (e) {
        console.warn('[ReportWriter] signature resolve failed:', e.message);
      }
    };

    syncFromStorage();
    resolveAuthenticatedImages();

    const onSettingsUpdated = () => {
      syncFromStorage();
      resolveAuthenticatedImages();
    };

    window.addEventListener('settings-updated', onSettingsUpdated);
    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', onSettingsUpdated);
    document.addEventListener('visibilitychange', onSettingsUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('settings-updated', onSettingsUpdated);
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', onSettingsUpdated);
      document.removeEventListener('visibilitychange', onSettingsUpdated);
    };
  }, [userProfile]);

  useEffect(() => {
    const savedCols = localStorage.getItem('echion_table_cols');
    const savedRows = localStorage.getItem('echion_table_rows');
    if (savedCols) setTableCols(parseInt(savedCols, 10));
    if (savedRows) setTableRows(parseInt(savedRows, 10));
  }, []);

  useEffect(() => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadInitialData = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn(
          '[ReportWriter] No authToken in localStorage — skipping backend fetch'
        );
        return;
      }
      try {
        const tplRes = await api.getTemplates();
        const tplList = tplRes?.data?.content || tplRes?.data || [];
        if (!cancelled) {
          setTemplates(Array.isArray(tplList) ? tplList : []);
        }
      } catch (err) {
        console.error('[ReportWriter] getTemplates failed:', err.message);
      }
    };
    loadInitialData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleTableInteract = (e) => {
      if (e.target.closest('.table-ui-menu')) return;
      const td = e.target.closest('td');
      if (td) {
        const table = td.closest('table');
        let qRef = null;
        if (td.closest('#editor-findings')) qRef = findingsRef;
        else if (td.closest('#editor-impression')) qRef = impressionRef;
        else if (td.closest('#editor-recommendation')) qRef = recommendationRef;
        if (table && qRef) {
          const rect = table.getBoundingClientRect();
          setTableUI({
            visible: true,
            top: rect.top - 12,
            left: rect.left - 12,
            quillRef: qRef,
            tableNode: table,
          });
          setTableMenuOpen(false);
          return;
        }
      }
      setTableUI((prev) => ({ ...prev, visible: false }));
      setTableMenuOpen(false);
    };

    const updateOverlayPos = () => {
      setTableUI((prev) => {
        if (prev.visible && prev.tableNode) {
          const rect = prev.tableNode.getBoundingClientRect();
          return { ...prev, top: rect.top - 12, left: rect.left - 12 };
        }
        return prev;
      });
    };

    document.addEventListener('mousedown', handleTableInteract);
    window.addEventListener('scroll', updateOverlayPos, true);
    window.addEventListener('resize', updateOverlayPos);

    return () => {
      document.removeEventListener('mousedown', handleTableInteract);
      window.removeEventListener('scroll', updateOverlayPos, true);
      window.removeEventListener('resize', updateOverlayPos);
    };
  }, []);

  const loadState = (key, fallback) => {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  };

  const [demographics, setDemographics] = useState(() =>
    loadState('rw_demographics', {
      fullName: '',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      }),
      age: '',
      sex: 'FEMALE',
      clinicalHistory: '',
    })
  );

  const [scanDetails, setScanDetails] = useState(() =>
    loadState('rw_scanDetails', {
      scanType: 'ABDOMINAL',
      reportType: 'NORMAL',
    })
  );

  const [content, setContent] = useState(() =>
    loadState('rw_content', {
      findings: '',
      impression: '',
      recommendation: '',
    })
  );

  const [signReport, setSignReport] = useState(true);
  const [isEditingPreview, setIsEditingPreview] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    localStorage.setItem('rw_demographics', JSON.stringify(demographics));
  }, [demographics]);
  useEffect(() => {
    localStorage.setItem('rw_scanDetails', JSON.stringify(scanDetails));
  }, [scanDetails]);
  useEffect(() => {
    localStorage.setItem('rw_content', JSON.stringify(content));
  }, [content]);

  const handleDemographicsChange = (e) => {
    const { name, value } = e.target;
    setDemographics((prev) => ({ ...prev, [name]: value }));
  };

  const handleScanTypeChange = (e) => {
    setScanDetails((prev) => ({ ...prev, scanType: e.target.value }));
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    setContent((prev) => ({
      ...prev,
      findings: t.defaultFindings || prev.findings,
      impression: t.defaultImpression || prev.impression,
    }));
    setScanDetails((prev) => ({
      ...prev,
      scanType: t.scanType || prev.scanType,
      reportType: t.reportType || prev.reportType,
    }));
  };

  const handleSaveReport = async () => {
    if (!demographics.age || !scanDetails.scanType) {
      alert('Please fill in at least Age and Scan Type.');
      return;
    }
    if (!demographics.fullName || !demographics.fullName.trim()) {
      alert('Please provide a patient name.');
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('You are not logged in. Please log in first.');
      return;
    }

    setIsSaving(true);
    try {
      const reportPayload = {
        patientName: demographics.fullName.trim(),
        patientAge: parseInt(demographics.age, 10) || 0,
        patientSex: demographics.sex || 'FEMALE',
        patientId: '',
        scanDate: new Date().toISOString().split('T')[0],
        scanType: scanDetails.scanType,
        reportType: scanDetails.reportType,
        clinicalHistory: demographics.clinicalHistory || '',
        findings: content.findings || '',
        impression: content.impression || '',
        recommendation: content.recommendation || '',
        ...(selectedTemplateId ? { templateId: selectedTemplateId } : {}),
      };
      const response = await api.saveReport(reportPayload);
      const reportId = response?.data?.id || response?.id || 'unknown';
      alert(`Report saved successfully! Report ID: ${reportId}`);
      ['rw_demographics', 'rw_scanDetails', 'rw_content'].forEach((k) =>
        localStorage.removeItem(k)
      );
      setDemographics({
        fullName: '',
        date: new Date().toLocaleDateString(),
        age: '',
        sex: 'FEMALE',
        clinicalHistory: '',
      });
      setScanDetails({ scanType: 'ABDOMINAL', reportType: 'NORMAL' });
      setContent({ findings: '', impression: '', recommendation: '' });
      setSelectedTemplateId('');
    } catch (error) {
      let msg = error.message;
      if (/permission|403/i.test(msg)) {
        msg = 'Access denied. Your role may not have permission to save reports.';
      }
      alert('Error saving report: ' + msg);
    } finally {
      setIsSaving(false);
    }
  };

  const generatePdfBlob = async () => {
    const input = document.getElementById('printable-report-content');
    if (!input) {
      alert('Error: Report template not found.');
      return null;
    }
    const wasEditing = isEditingPreview;
    if (wasEditing) setIsEditingPreview(false);

    const originalStyles = {
      maxWidth: input.style.maxWidth,
      width: input.style.width,
      margin: input.style.margin,
      boxShadow: input.style.boxShadow,
      transform: input.style.transform,
      padding: input.style.padding,
    };

    input.style.maxWidth = 'none';
    input.style.width = '794px';
    input.style.minHeight = '1123px';
    input.style.margin = '0';
    input.style.padding = '50px 60px';
    input.style.boxShadow = 'none';
    input.style.transform = 'none';

    await new Promise((resolve) => setTimeout(resolve, 150));
    try {
      const canvas = await html2canvas(input, {
        scale: 3,
        useCORS: false,     // blob: URLs are same-origin — no CORS needed
        allowTaint: true,   // allow blob: and inline images to render
        logging: false,
        windowWidth: 794,
        backgroundColor: '#ffffff',
      });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 1.0),
        'JPEG',
        0, 0, pdfWidth, pdfHeight
      );

      Object.assign(input.style, originalStyles);
      if (wasEditing) setIsEditingPreview(true);
      return pdf.output('blob');
    } catch (e) {
      Object.assign(input.style, originalStyles);
      if (wasEditing) setIsEditingPreview(true);
      alert('Failed to generate PDF.');
      return null;
    }
  };

  const handlePrint = async () => {
    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow popups for this site.');
      return;
    }
    win.document.write(
      '<div style="font-family:sans-serif;text-align:center;margin-top:50px;">Generating PDF…</div>'
    );
    const blob = await generatePdfBlob();
    if (!blob) { win.close(); return; }
    win.location.href = URL.createObjectURL(blob);
  };

  const handleExportPDF = async () => {
    setIsExportModalOpen(false);
    const blob = await generatePdfBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${demographics.fullName || 'Report'}_Ultrasound.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEmailReport = async () => {
    setIsExportModalOpen(false);
    const blob = await generatePdfBlob();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${demographics.fullName || 'Report'}_Ultrasound.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    const subject = encodeURIComponent(
      `Ultrasound Report: ${demographics.fullName || 'Patient'}`
    );
    const body = encodeURIComponent(
      `Please find the ultrasound report for ${
        demographics.fullName || 'Patient'
      } attached.\n\n*Note to sender: The report has been downloaded to your device. Please manually attach it to this email before sending.*`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyToClipboard = async () => {
    setIsExportModalOpen(false);
    if (contentToCopyRef.current) {
      try {
        const htmlContent = contentToCopyRef.current.innerHTML;
        const textContent = contentToCopyRef.current.innerText;
        if (window.ClipboardItem) {
          const clipboardItem = new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([textContent], { type: 'text/plain' }),
          });
          await navigator.clipboard.write([clipboardItem]);
        } else {
          await navigator.clipboard.writeText(textContent);
        }
        alert('Report content successfully copied to clipboard!');
      } catch (err) {
        try {
          await navigator.clipboard.writeText(
            contentToCopyRef.current.innerText
          );
          alert('Report text copied to clipboard (plain text)!');
        } catch (fallbackErr) {
          alert('Failed to copy text to clipboard.');
        }
      }
    }
  };

  const executeInsertTable = () => {
    const quill = tableModal.quill;
    if (quill && tableRows > 0 && tableCols > 0) {
      if (rememberDims) {
        localStorage.setItem('echion_table_cols', tableCols);
        localStorage.setItem('echion_table_rows', tableRows);
      } else {
        localStorage.removeItem('echion_table_cols');
        localStorage.removeItem('echion_table_rows');
      }
      const tableWidth = '100%';
      let tableHTML = `<table style="width: ${tableWidth}; border-collapse: collapse; border: 1px solid #cbd5e1; margin-bottom: 10px;"><tbody>`;
      for (let i = 0; i < tableRows; i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < tableCols; j++) {
          tableHTML +=
            '<td style="border: 1px solid #cbd5e1; padding: 8px; min-width: 50px;">&nbsp;</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</tbody></table><p><br></p>';
      const range = quill.getSelection(true);
      quill.clipboard.dangerouslyPasteHTML(
        range ? range.index : quill.getLength(),
        tableHTML
      );
    }
    setTableModal({ isOpen: false, quill: null });
  };

  const handleTableAction = (action) => {
    const quill = tableUI.quillRef?.current?.getEditor();
    const table = tableUI.tableNode;
    const selection = window.getSelection();
    if (!quill || !table) return;
    let td = null;
    let tr = null;
    if (selection.anchorNode) {
      const node = selection.anchorNode;
      td =
        node.nodeType === 1
          ? node.closest('td')
          : node.parentElement?.closest('td');
      tr = td?.closest('tr');
    }
    if (!td || !tr) {
      tr = table.rows[0];
      td = tr?.cells[0];
    }
    if (!tr || !td) return;
    const cellIndex = Array.from(tr.children).indexOf(td);
    const rowIndex = Array.from(table.rows).indexOf(tr);

    if (action === 'deleteRow') {
      table.deleteRow(rowIndex);
      if (table.rows.length === 0) table.remove();
    } else if (action === 'deleteColumn') {
      Array.from(table.rows).forEach((row) => row.deleteCell(cellIndex));
      if (table.rows[0]?.children.length === 0) table.remove();
    } else if (action === 'deleteTable') {
      table.remove();
      setTableUI((prev) => ({ ...prev, visible: false }));
    } else if (action === 'insertRowAbove' || action === 'insertRowBelow') {
      const newRow = table.insertRow(
        action === 'insertRowAbove' ? rowIndex : rowIndex + 1
      );
      for (let i = 0; i < tr.children.length; i++) {
        const newCell = newRow.insertCell(i);
        newCell.innerHTML = '&nbsp;';
        newCell.style.border = '1px solid #cbd5e1';
        newCell.style.padding = '8px';
        newCell.style.minWidth = '50px';
      }
    } else if (
      action === 'insertColumnLeft' ||
      action === 'insertColumnRight'
    ) {
      const insertIdx =
        action === 'insertColumnLeft' ? cellIndex : cellIndex + 1;
      Array.from(table.rows).forEach((row) => {
        const newCell = row.insertCell(insertIdx);
        newCell.innerHTML = '&nbsp;';
        newCell.style.border = '1px solid #cbd5e1';
        newCell.style.padding = '8px';
        newCell.style.minWidth = '50px';
      });
    }
    quill.update('user');
    setTableMenuOpen(false);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: Font.whitelist }, { size: toolbarSizes }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ script: 'sub' }, { script: 'super' }],
          [{ color: [] }, { background: [] }],
          [
            { list: 'ordered' },
            { list: 'bullet' },
            { indent: '-1' },
            { indent: '+1' },
          ],
          [{ align: [] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['insertTable'],
          ['clean'],
        ],
        handlers: {
          insertTable: function () {
            setTableModal({ isOpen: true, quill: this.quill });
          },
        },
      },
      table: true,
    }),
    []
  );

  const activeLabel =
    SCAN_ENUMS.find((s) => s.value === scanDetails.scanType)?.label ||
    'Abdominal';

  const stackPanels = isMobile || isTablet;
  const containerDirection = stackPanels ? 'column' : 'row';
  const leftPaneFlex = stackPanels
    ? 'none'
    : isSmallDesktop
    ? '1 1 55%'
    : '1.2';
  const rightPaneFlex = stackPanels
    ? 'none'
    : isSmallDesktop
    ? '1 1 45%'
    : '0.8';
  const outerHeight = stackPanels ? 'auto' : 'calc(100vh - 100px)';
  const paneMinHeight = stackPanels ? 'auto' : '100%';

  const buttonRowDirection = isMobile ? 'column' : 'row';
  const buttonRowAlign = isMobile ? 'stretch' : 'space-between';

  const availableTemplates = templates.filter(
    (t) => t.scanType === scanDetails.scanType
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: containerDirection,
        gap: isMobile ? '12px' : '20px',
        height: outerHeight,
        minHeight: '100vh',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        padding: isMobile ? '8px' : '10px',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      <style>{`
        .ql-editor {
          min-height: 150px;
          overflow-wrap: break-word !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
        }
        .ql-toolbar.ql-snow {
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background: #f1f5f9;
          border-color: #e2e8f0;
          flex-wrap: wrap;
        }
        .ql-container.ql-snow {
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
          border-color: #e2e8f0;
        }
        .export-option:hover {
          background-color: #f8fafc;
          border-color: #cbd5e1;
        }
        .ql-snow .ql-toolbar button,
        .ql-snow .ql-toolbar .ql-picker { position: relative !important; }
        .ql-snow .ql-toolbar button::after,
        .ql-snow .ql-toolbar .ql-picker::after {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 5px;
          background: #1e293b;
          color: #f8fafc;
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: opacity 0.2s ease, visibility 0.2s ease;
          z-index: 1000;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        .ql-snow .ql-toolbar button:hover::after,
        .ql-snow .ql-toolbar .ql-picker:hover::after {
          opacity: 1;
          visibility: visible;
        }
        .ql-snow .ql-toolbar .ql-font::after { content: "Font Style"; }
        .ql-snow .ql-toolbar .ql-size::after { content: "Font Size"; }
        .ql-snow .ql-toolbar .ql-bold::after { content: "Bold"; }
        .ql-snow .ql-toolbar .ql-italic::after { content: "Italic"; }
        .ql-snow .ql-toolbar .ql-underline::after { content: "Underline"; }
        .ql-snow .ql-toolbar .ql-strike::after { content: "Strikethrough"; }
        .ql-snow .ql-toolbar .ql-script[value="sub"]::after { content: "Subscript"; }
        .ql-snow .ql-toolbar .ql-script[value="super"]::after { content: "Superscript"; }
        .ql-snow .ql-toolbar .ql-color::after { content: "Text Color"; }
        .ql-snow .ql-toolbar .ql-background::after { content: "Highlight Color"; }
        .ql-snow .ql-toolbar .ql-list[value="ordered"]::after { content: "Numbered List"; }
        .ql-snow .ql-toolbar .ql-list[value="bullet"]::after { content: "Bulleted List"; }
        .ql-snow .ql-toolbar .ql-indent[value="-1"]::after { content: "Decrease Indent"; }
        .ql-snow .ql-toolbar .ql-indent[value="+1"]::after { content: "Increase Indent"; }
        .ql-snow .ql-toolbar .ql-align::after { content: "Text Alignment"; }
        .ql-snow .ql-toolbar .ql-header::after { content: "Format / Heading"; }
        .ql-snow .ql-toolbar button.ql-insertTable::after { content: "Insert Table"; }
        .ql-snow .ql-toolbar .ql-clean::after { content: "Clear Formatting"; }
        ${customSizes
          .map(
            (size) => `
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before {
            content: '${size.replace('px', '')}' !important;
          }
        `
          )
          .join('\n')}
        .table-menu-btn {
          padding: 8px 15px;
          border: none;
          background: white;
          text-align: left;
          font-size: 13px;
          cursor: pointer;
          color: #334155;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .table-menu-btn:hover { background-color: #f1f5f9; }
        .ql-snow .ql-toolbar button.ql-insertTable {
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="%23444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>');
          background-size: 16px;
          background-repeat: no-repeat;
          background-position: center;
        }
        .ql-snow .ql-toolbar button.ql-insertTable svg { display: none; }
        .ql-editor table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        .ql-editor td, .ql-editor th {
          border: 1px solid #cbd5e1;
          padding: 8px;
        }
        .ql-editor { font-size: 12px; }
        .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before,
        .ql-snow .ql-picker.ql-size .ql-picker-item:not([data-value])::before {
          content: '12' !important;
        }
        @media (max-width: 640px) {
          .ql-toolbar.ql-snow { padding: 4px !important; }
          .ql-snow .ql-toolbar button,
          .ql-snow .ql-toolbar .ql-picker {
            width: 26px !important;
            height: 22px !important;
          }
          .ql-snow .ql-toolbar button svg,
          .ql-snow .ql-toolbar .ql-picker svg {
            width: 14px !important;
            height: 14px !important;
          }
          .ql-snow .ql-picker.ql-expanded .ql-picker-options {
            max-width: 220px;
          }
        }
      `}</style>

      <div
        style={{
          flex: leftPaneFlex,
          width: stackPanels ? '100%' : undefined,
          maxWidth: '100%',
          background: '#ffffff',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '30px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          overflowY: stackPanels ? 'visible' : 'auto',
          border: '1px solid #eef2f6',
          height: paneMinHeight,
          boxSizing: 'border-box',
          minWidth: 0,
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '20px',
            flexWrap: 'wrap',
          }}
        >
          <img
            src={process.env.PUBLIC_URL + '/echionLOGO.png'}
            alt="Logo"
            style={{
              width: isMobile ? '40px' : '50px',
              height: isMobile ? '40px' : '50px',
              objectFit: 'contain',
            }}
          />
          <h2
            style={{
              margin: 0,
              color: '#0f172a',
              fontSize: isMobile ? '1.4rem' : '1.8rem',
              fontWeight: '800',
            }}
          >
            New Report
          </h2>
        </div>
        <p
          style={{
            color: '#64748b',
            fontSize: '0.9rem',
            marginBottom: '30px',
          }}
        >
          Create a new ultrasound report by entering patient details and your
          findings.
        </p>

        <h3 style={sectionHeader}>Patient Information</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? '1fr'
              : isTablet
              ? '1fr 1fr'
              : '2fr 1fr 1fr',
            gap: '15px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              gridColumn: isMobile
                ? 'span 1'
                : isTablet
                ? 'span 2'
                : 'span 1',
            }}
          >
            <label style={labelStyle}>Full Name</label>
            <input
              type="text"
              name="fullName"
              value={demographics.fullName || ''}
              onChange={handleDemographicsChange}
              style={inputStyle}
              spellCheck={true}
              data-gramm="true"
              data-gramm_editor="true"
              data-enable-grammarly="true"
            />
          </div>
          <div>
            <label style={labelStyle}>Sex</label>
            <select
              name="sex"
              value={demographics.sex || 'FEMALE'}
              onChange={handleDemographicsChange}
              style={inputStyle}
            >
              <option value="FEMALE">Female</option>
              <option value="MALE">Male</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age</label>
            <input
              type="text"
              name="age"
              value={demographics.age || ''}
              onChange={handleDemographicsChange}
              style={inputStyle}
              spellCheck={true}
              data-gramm="true"
              data-gramm_editor="true"
              data-enable-grammarly="true"
            />
          </div>
        </div>
        <div style={{ marginBottom: '25px' }}>
          <label style={labelStyle}>Clinical History</label>
          <textarea
            rows="2"
            name="clinicalHistory"
            value={demographics.clinicalHistory || ''}
            onChange={handleDemographicsChange}
            style={{ ...inputStyle, resize: 'vertical' }}
            spellCheck={true}
            data-gramm="true"
            data-gramm_editor="true"
            data-enable-grammarly="true"
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '15px',
            marginBottom: '20px',
          }}
        >
          <div>
            <label style={labelStyle}>Select Type of Scan</label>
            <select
              value={scanDetails.scanType || ''}
              onChange={handleScanTypeChange}
              style={inputStyle}
            >
              {SCAN_ENUMS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Select Report Type</label>
            <select
              value={scanDetails.reportType || ''}
              onChange={(e) =>
                setScanDetails((prev) => ({
                  ...prev,
                  reportType: e.target.value,
                }))
              }
              style={inputStyle}
            >
              <option value="NORMAL">Normal</option>
              <option value="PATHOLOGY">Pathology</option>
              <option value="DIAGNOSTIC">Diagnostic</option>
            </select>
          </div>
        </div>

        {availableTemplates.length > 0 && (
          <div style={{ marginBottom: '25px' }}>
            <label style={labelStyle}>
              Load from Saved Template ({availableTemplates.length} available)
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              style={inputStyle}
            >
              <option value="">— None (start blank) —</option>
              {availableTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '40px' }}>
          <label style={labelStyle}>Findings</label>
          <div id="editor-findings" style={{ paddingTop: '5px' }}>
            <ReactQuill
              ref={findingsRef}
              theme="snow"
              value={content.findings || ''}
              onChange={(val) =>
                setContent((prev) => ({ ...prev, findings: val }))
              }
              modules={modules}
              style={{ background: '#f8fafc', borderRadius: '6px' }}
              spellCheck={true}
            />
          </div>
        </div>
        <div style={{ marginTop: '50px', marginBottom: '40px' }}>
          <label style={labelStyle}>Impression</label>
          <div id="editor-impression" style={{ paddingTop: '5px' }}>
            <ReactQuill
              ref={impressionRef}
              theme="snow"
              value={content.impression || ''}
              onChange={(val) =>
                setContent((prev) => ({ ...prev, impression: val }))
              }
              modules={modules}
              style={{ background: '#f8fafc', borderRadius: '6px' }}
              spellCheck={true}
            />
          </div>
        </div>
        <div style={{ marginTop: '50px', marginBottom: '40px' }}>
          <label style={labelStyle}>Recommendations</label>
          <div id="editor-recommendation" style={{ paddingTop: '5px' }}>
            <ReactQuill
              ref={recommendationRef}
              theme="snow"
              value={content.recommendation || ''}
              onChange={(val) =>
                setContent((prev) => ({ ...prev, recommendation: val }))
              }
              modules={modules}
              style={{ background: '#f8fafc', borderRadius: '6px' }}
              spellCheck={true}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '30px',
            marginTop: '50px',
            paddingTop: '15px',
          }}
        >
          <input
            type="checkbox"
            checked={signReport}
            onChange={(e) => setSignReport(e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
          />
          <span
            style={{ fontSize: '0.95rem', color: '#0f172a', fontWeight: '600' }}
          >
            {(clinicianName || 'Dr. John Doe') + ' '}
            <span style={{ color: '#64748b', fontWeight: '400' }}>
              {clinicianDesignation
                ? clinicianDesignation.replace('_', ' ')
                : 'Radiologist'}
            </span>
          </span>
        </div>
      </div>

      <div
        style={{
          flex: rightPaneFlex,
          width: stackPanels ? '100%' : undefined,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          height: stackPanels ? 'auto' : '100%',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <h3
          style={{
            margin: '0 0 5px 0',
            color: '#334155',
            fontSize: '1.2rem',
            fontWeight: '600',
          }}
        >
          Preview{' '}
          {isEditingPreview && (
            <span
              style={{
                color: '#eab308',
                fontSize: '0.9rem',
                marginLeft: '10px',
              }}
            >
              (Editing Mode Active)
            </span>
          )}
        </h3>

        <div
          ref={previewContainerRef}
          style={{
            flex: 1,
            background: '#f8fafc',
            borderRadius: '12px',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
            border: '1px solid #e2e8f0',
            overflowY: 'auto',
            overflowX: 'auto',
            minHeight: stackPanels ? '400px' : 'auto',
            maxWidth: '100%',
          }}
        >
          <div
            id="printable-report-content"
            contentEditable={isEditingPreview}
            suppressContentEditableWarning={true}
            spellCheck={true}
            style={{
              width: '100%',
              minWidth: isMobile ? '0' : '600px',
              background: 'white',
              color: '#000000',
              padding: isMobile ? '16px' : '30px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              outline: isEditingPreview ? '2px dashed #3b82f6' : 'none',
              cursor: isEditingPreview ? 'text' : 'default',
              transition: 'all 0.2s',
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              boxSizing: 'border-box',
            }}
          >
            <div
              contentEditable={false}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                paddingBottom: '15px',
                marginBottom: '20px',
              }}
            >
              {letterheadUrl ? (
                <img
                  key={letterheadUrl}
                  src={letterheadUrl}
                  alt="Letterhead"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100px',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    console.warn(
                      '[ReportWriter] Letterhead failed to load:',
                      letterheadUrl
                    );
                    setLetterheadUrl('');
                  }}
                />
              ) : (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                    }}
                  >
                    <img
                      src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                      alt="Logo"
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'contain',
                      }}
                    />
                    <div>
                      <h1
                        style={{
                          margin: 0,
                          color: '#1e293b',
                          fontSize: '18pt',
                          fontWeight: '800',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {hospitalName || 'Echion Health'}
                      </h1>
                      <p
                        style={{
                          margin: 0,
                          color: '#64748b',
                          fontSize: '9pt',
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Sonoscribe
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: isMobile ? 'left' : 'right',
                      fontSize: '9pt',
                      color: '#334155',
                      lineHeight: '1.5',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>
                      Contact Information
                    </div>
                    <div>📞 +233 54 843 2033</div>
                    <div style={{ color: '#2563eb' }}>
                      www.echionhealth.com
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ overflowX: 'auto', marginBottom: '25px' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: '420px',
                  borderCollapse: 'collapse',
                  fontSize: '10pt',
                  border: '1px solid #e2e8f0',
                }}
              >
                <tbody>
                  <tr>
                    <td
                      style={{
                        padding: '8px 12px',
                        background: '#f8fafc',
                        fontWeight: 'bold',
                        width: '20%',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      Patient Name:
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        width: '30%',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {demographics.fullName?.toUpperCase() || 'N/A'}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        background: '#f8fafc',
                        fontWeight: 'bold',
                        width: '20%',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      Sex:
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        width: '30%',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {demographics.sex?.toUpperCase() || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        padding: '8px 12px',
                        background: '#f8fafc',
                        fontWeight: 'bold',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      Age:
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {demographics.age || 'N/A'} YRS
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        background: '#f8fafc',
                        fontWeight: 'bold',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      Report Date:
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {demographics.date?.toUpperCase() || 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div ref={contentToCopyRef}>
              <h2
                style={{
                  textAlign: 'center',
                  margin: '0 0 20px 0',
                  fontSize: '12pt',
                  letterSpacing: '1.5px',
                  color: '#1e293b',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  wordBreak: 'break-word',
                }}
              >
                ULTRASOUND {activeLabel?.toUpperCase() || 'ABDOMINAL'} REPORT
              </h2>

              <div
                style={{
                  fontSize: '10pt',
                  lineHeight: '1.6',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                <div style={{ marginBottom: '20px' }}>
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#1e293b',
                      textTransform: 'uppercase',
                      fontSize: '10pt',
                    }}
                  >
                    Clinical Information:
                  </span>
                  <span style={{ marginLeft: '8px' }}>
                    {demographics.clinicalHistory || 'N/A'}
                  </span>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4
                    style={{
                      color: '#1e293b',
                      borderBottom: '1px solid #f1f5f9',
                      paddingBottom: '4px',
                      marginBottom: '10px',
                      fontSize: '10pt',
                      textTransform: 'uppercase',
                    }}
                  >
                    Findings
                  </h4>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: content.findings || '' }}
                    style={{
                      padding: 0,
                      minHeight: 'auto',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4
                    style={{
                      color: '#1e293b',
                      borderBottom: '1px solid #f1f5f9',
                      paddingBottom: '4px',
                      marginBottom: '10px',
                      fontSize: '10pt',
                      textTransform: 'uppercase',
                    }}
                  >
                    Impression
                  </h4>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: content.impression || '' }}
                    style={{
                      padding: 0,
                      minHeight: 'auto',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <h4
                    style={{
                      color: '#1e293b',
                      borderBottom: '1px solid #f1f5f9',
                      paddingBottom: '4px',
                      marginBottom: '10px',
                      fontSize: '10pt',
                      textTransform: 'uppercase',
                    }}
                  >
                    Recommendations
                  </h4>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: content.recommendation || '' }}
                    style={{
                      padding: 0,
                      minHeight: 'auto',
                      fontSize: '12px',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              contentEditable={false}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '30px',
                paddingTop: '20px',
              }}
            >
              <div style={{ textAlign: 'center', width: '220px' }}>
                {signReport ? (
                  signatureUrl ? (
                    <div
                      style={{
                        height: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        key={signatureUrl}
                        src={signatureUrl}
                        alt="Signature"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          console.warn(
                            '[ReportWriter] Signature failed to load:',
                            signatureUrl
                          );
                          setSignatureUrl('');
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        height: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontFamily:
                            "'Brush Script MT', 'Cedarville Cursive', cursive",
                          fontSize: '20pt',
                          color: '#1e293b',
                        }}
                      >
                        {clinicianName || 'John Doe'}
                      </span>
                    </div>
                  )
                ) : (
                  <div style={{ height: '60px' }}></div>
                )}
                <div style={{ paddingTop: '5px', marginTop: '5px' }}>
                  <strong
                    style={{
                      display: 'block',
                      fontSize: '10pt',
                      color: '#000',
                    }}
                  >
                    {clinicianName ? `Dr. ${clinicianName}` : 'Dr. John Doe'}
                  </strong>
                  <span style={{ fontSize: '9pt', color: '#475569' }}>
                    {clinicianDesignation
                      ? clinicianDesignation.replace('_', ' ')
                      : 'Consultant Radiologist'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: buttonRowDirection,
            gap: '10px',
            justifyContent: buttonRowAlign,
            alignItems: 'stretch',
            background: '#ffffff',
            padding: isMobile ? '10px' : '15px',
            borderRadius: '8px',
            border: '1px solid #eef2f6',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              flex: isMobile ? '1 1 100%' : '0 0 auto',
            }}
          >
            <button
              onClick={() => setIsEditingPreview(!isEditingPreview)}
              style={{
                ...actionBtnStyle,
                background: isEditingPreview ? '#f1f5f9' : 'white',
                borderColor: isEditingPreview ? '#94a3b8' : '#e2e8f0',
                flex: isMobile ? '1 1 auto' : '0 0 auto',
                justifyContent: 'center',
              }}
            >
              {isEditingPreview ? 'Stop Editing' : 'Edit'}
            </button>
            <button
              onClick={handlePrint}
              style={{
                ...actionBtnStyle,
                flex: isMobile ? '1 1 auto' : '0 0 auto',
                justifyContent: 'center',
              }}
            >
              Print
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              flex: isMobile ? '1 1 100%' : '0 0 auto',
            }}
          >
            <button
              onClick={handleSaveReport}
              disabled={isSaving}
              style={{
                ...actionBtnStyle,
                background: isSaving ? '#93c5fd' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '6px' : '6px 0 0 6px',
                borderRight: isMobile
                  ? 'none'
                  : '1px solid rgba(255,255,255,0.2)',
                flex: isMobile ? '1 1 50%' : '0 0 auto',
                justifyContent: 'center',
                marginBottom: isMobile ? '8px' : 0,
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => setIsExportModalOpen(true)}
              style={{
                ...actionBtnStyle,
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '6px' : '0 6px 6px 0',
                flex: isMobile ? '1 1 50%' : '0 0 auto',
                justifyContent: 'center',
              }}
            >
              Export
            </button>
          </div>
        </div>
      </div>

      {tableUI.visible && (
        <div
          className="table-ui-menu"
          style={{
            position: 'fixed',
            top: tableUI.top,
            left: tableUI.left,
            zIndex: 10000,
          }}
        >
          <div
            onClick={() => setTableMenuOpen(!tableMenuOpen)}
            onMouseDown={(e) => e.preventDefault()}
            style={{
              width: '24px',
              height: '24px',
              background: 'white',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
              color: '#475569',
            }}
            title="Table Options"
          >
            +
          </div>

          {tableMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                width: '200px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertRowAbove')}
              >
                Insert Row Above
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertRowBelow')}
              >
                Insert Row Below
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertColumnLeft')}
              >
                Insert Column Left
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('insertColumnRight')}
              >
                Insert Column Right
              </button>
              <div
                style={{
                  height: '1px',
                  background: '#e2e8f0',
                  margin: '4px 0',
                }}
              ></div>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteRow')}
                style={{ color: '#ef4444' }}
              >
                Delete Row
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteColumn')}
                style={{ color: '#ef4444' }}
              >
                Delete Column
              </button>
              <button
                className="table-menu-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleTableAction('deleteTable')}
                style={{ color: '#ef4444', fontWeight: 'bold' }}
              >
                Delete Table
              </button>
            </div>
          )}
        </div>
      )}

      {tableModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.2)',
            padding: '12px',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              backgroundColor: '#f0f0f0',
              border: '1px solid #999',
              borderRadius: '6px',
              width: '100%',
              maxWidth: '300px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              fontFamily: '"Segoe UI", sans-serif',
              fontSize: '12px',
              color: '#333',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 15px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #ccc',
              }}
            >
              <span style={{ fontSize: '13px', color: '#000' }}>
                Insert Table
              </span>
              <button
                onClick={() => setTableModal({ isOpen: false, quill: null })}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '15px' }}>
              <div style={{ marginBottom: '8px' }}>Table size</div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                }}
              >
                <span>Columns:</span>
                <input
                  type="number"
                  value={tableCols}
                  onChange={(e) => setTableCols(e.target.value)}
                  style={{ width: '60px' }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '15px',
                }}
              >
                <span>Rows:</span>
                <input
                  type="number"
                  value={tableRows}
                  onChange={(e) => setTableRows(e.target.value)}
                  style={{ width: '60px' }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <input
                    type="checkbox"
                    checked={rememberDims}
                    onChange={(e) => setRememberDims(e.target.checked)}
                  />
                  Remember dimensions
                </label>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <button
                  onClick={executeInsertTable}
                  style={{ padding: '5px 25px' }}
                >
                  OK
                </button>
                <button
                  onClick={() => setTableModal({ isOpen: false, quill: null })}
                  style={{ padding: '5px 25px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isExportModalOpen && (
        <div style={modalOverlayStyle}>
          <div
            style={{ ...modalContentStyle, maxHeight: '90vh', overflowY: 'auto' }}
          >
            <h3 style={{ fontSize: '0.85rem', marginBottom: '15px' }}>
              Export to Other Formats
            </h3>

            <div
              className="export-option"
              style={exportOptionStyle}
              onClick={handleCopyToClipboard}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600' }}>Copy to Clipboard</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Copy text from Title to Recommendation.
                </div>
              </div>
              <div style={{ color: '#94a3b8' }}>›</div>
            </div>

            <div
              className="export-option"
              style={exportOptionStyle}
              onClick={handleExportPDF}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600' }}>PDF Export</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Download the report as a PDF file.
                </div>
              </div>
              <div style={{ color: '#94a3b8' }}>›</div>
            </div>

            <div
              className="export-option"
              style={exportOptionStyle}
              onClick={handleEmailReport}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600' }}>Email Report</div>
                <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  Send the report via email.
                </div>
              </div>
              <div style={{ color: '#94a3b8' }}>›</div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '10px',
              }}
            >
              <button
                onClick={() => setIsExportModalOpen(false)}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const sectionHeader = {
  color: '#0f172a',
  fontSize: '1rem',
  fontWeight: '600',
  marginBottom: '15px',
};
const labelStyle = {
  fontWeight: '600',
  display: 'block',
  marginBottom: '10px',
  fontSize: '0.85rem',
  color: '#475569',
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '0.9rem',
  color: '#0f172a',
  boxSizing: 'border-box',
  backgroundColor: '#f8fafc',
  outline: 'none',
  wordBreak: 'break-word',
};
const actionBtnStyle = {
  background: 'white',
  border: '1px solid #e2e8f0',
  color: '#475569',
  padding: '10px 16px',
  borderRadius: '6px',
  fontSize: '0.9rem',
  fontWeight: '600',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s',
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  backdropFilter: 'blur(2px)',
  padding: '12px',
  boxSizing: 'border-box',
};
const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '24px',
  width: '100%',
  maxWidth: '420px',
  boxShadow:
    '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
  boxSizing: 'border-box',
};
const exportOptionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  padding: '15px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  marginBottom: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '#ffffff',
  boxSizing: 'border-box',
};
const cancelButtonStyle = {
  padding: '10px 40px',
  backgroundColor: '#ffffff',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  color: '#0f172a',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '0.95rem',
  transition: 'background-color 0.2s',
};

export default ReportWriter;